import $ from "jquery";

/**
 * Check if a URL is cross-origin (points to a different domain)
 * @param {string} url - The URL to check
 * @returns {boolean} - True if the URL is cross-origin, false otherwise
 */
function isCrossOrigin(url) {
  const targetUrl = new URL(url, window.location.href);
  return targetUrl.origin !== window.location.origin;
}

/**
 * Read a localization string from the translations object
 * @param {Object} translations - The translations object
 * @param {string} key - The key to look up
 * @param {string} alternative - The fallback value if the key is not found
 * @returns {string} - The localized string
 */
function readLocalization(translations, key, alternative) {
  if (key in translations) {
    return translations[key];
  }
  // eslint-disable-next-line no-console
  console.warn(`Missing localization for key ${key}.`);
  return alternative;
}

/**
 * Create the dialog content HTML
 * @param {URL} targetUrl - The target URL
 * @param {Object} translations - The translations object
 * @returns {string} - The HTML for the dialog content
 */
function createDialogContent(targetUrl, translations) {
  const DEFAULT_CROSS_ORIGIN_WARNING_LOCALIZATION =
    "<p>The content you're trying to access is only available on the site: %1$s</p><p>If you're not automatically redirected, <button>click here</button>";

  const content = readLocalization(
    translations,
    "cross-origin-warning.content",
    DEFAULT_CROSS_ORIGIN_WARNING_LOCALIZATION
  ).replaceAll("%1$s", targetUrl.host);

  const closeButtonLabel = readLocalization(
    translations,
    "cross-origin-warning.close-button.label",
    "Close Dialog"
  );

  return `
    <div class="form-with-cross-origin-warning__box">
      <div class="form-with-cross-origin-warning__box-head">
        <button type="button" data-role="abort">
          <img
            alt="${closeButtonLabel}"
            class="form-with-cross-origin-warning__close-icon"
            src="/typo3conf/ext/sitepackage/Resources/Public/Icons/Website/X_white.svg"
          />
        </button>
      </div>
      <div class="form-with-cross-origin-warning__box-body">
        <div>${content}</div>
      </div>
    </div>
  `;
}

/**
 * Initialize the cross-origin warning functionality
 */
function initLinkWithCrossOriginWarning() {
  // Get translations from custom global object
  const translations = window["HEIDENHAIN_TRANSLATIONS"]
    ? window["HEIDENHAIN_TRANSLATIONS"]
    : {};

  // Find all links with the data-cross-origin-warning attribute
  const productLinks = document.querySelectorAll(
    'a[data-cross-origin-warning="true"]'
  );

  productLinks.forEach((link) => {
    link.addEventListener("click", (e) => {
      const target = e.currentTarget;
      const href = target.href;

      if (!isCrossOrigin(href)) {
        return; // Not cross-origin, proceed normally
      }

      e.preventDefault();

      const targetUrl = new URL(href, window.location.href);

      const dialogRoot = document.createElement("div");
      dialogRoot.classList.add("form-with-cross-origin-warning__backdrop");

      // Create dialog content
      dialogRoot.innerHTML = createDialogContent(targetUrl, translations);

      // Set up redirect timeout
      const redirectTimeout = window.setTimeout(() => {
        window.location.href = href;
      }, 3000);

      // Set up abort handler
      const handleAbort = () => {
        window.clearTimeout(redirectTimeout);
        dialogRoot.remove();
      };

      // Add dialog to the page
      document.body.appendChild(dialogRoot);

      // Set up event handlers for buttons
      dialogRoot.querySelectorAll("button").forEach((button) => {
        button.classList.add(
          "link",
          "font-weight-bold",
          "info-item__link",
          "align-items-center",
          "bg-transparent",
          "border-0",
          "text-primary"
        );

        if (button.dataset["role"] === "abort") {
          button.addEventListener("click", handleAbort);
        } else {
          button.addEventListener("click", () => {
            window.location.href = href;
          });
        }
      });
    });
  });
}

// Initialize when the DOM is ready
$(document).ready(initLinkWithCrossOriginWarning);

export default initLinkWithCrossOriginWarning;
